// useToolData.js
import { useState, useEffect, useCallback } from 'react';
import { getTool, getPlot, patchWidget, setDefaults } from '../utils/api';
import { PARAMETER_SCHEMA } from '../utils/globals';
import { useNavigate } from "react-router-dom";

export const useToolData = (id, tool, setCacheTrigger, pushApiCount, popApiCount) => {
  const navigate = useNavigate();
  const [toolData, setToolData] = useState(PARAMETER_SCHEMA);
  const [parameters, setParameters] = useState(PARAMETER_SCHEMA);
  const [modifications, setModifications] = useState(PARAMETER_SCHEMA);
  const [warnings, setWarnings] = useState(PARAMETER_SCHEMA);
  const [errors, setErrors] = useState(PARAMETER_SCHEMA);
  const [serverError, setServerError] = useState(false);

  const clearErrors = useCallback(() => {
    setCacheTrigger(Date.now());
    setErrors(PARAMETER_SCHEMA);
    setServerError(false)
  }, []);

  const handleErrorSetting = useCallback((error) => {
    clearErrors()
    popApiCount();
    if (error.response) {
      switch (error.response.status) {
        case 400:
          setErrors((prevState) => ({...prevState, ...error.response.data,}))
          return;
        case 500:
          setServerError(true)
          return;
        case 404:
          navigate('/404')
          return;
        default:
          navigate('/500')
          return;
      }
    } else if (errors.request) {
      navigate('/500')
    } else {
      navigate('/500')
    }
  }, [navigate]);

  const handleDataSetting = useCallback((tool, widgets) => {
    clearErrors();
    popApiCount();
    setToolData(tool || {});
    setParameters(widgets || PARAMETER_SCHEMA);
  }, []);

  const handleDataUpdating = useCallback((tool, mods, warns) => {
    clearErrors();
    popApiCount()
    if (tool) {
      setToolData((prev) => ({ ...prev, ...tool }));
    }
    if (mods) {
      setModifications((prev) => ({ ...prev, ...mods }));
    }
    if (warns) {
      setWarnings((prev) => ({ ...prev, ...warns }));
    }
  }, [clearErrors]);

  const multipleWidgetSetter = useCallback((params) => {
    setParameters(prevData => {
      const newData = { ...prevData };
      Object.keys(params).forEach((outerKey) => {
        newData[outerKey] = {
          ...prevData[outerKey],
          ...params[outerKey],
        };
      });
      return newData;
    });
  }, []);

  const fetchData = useCallback(() => {
    if (id !== 'none') {
      pushApiCount();
      getTool(id, tool)
        .then((response) => {
          handleDataSetting(response.data.tool, response.data.widgets);
          pushApiCount();
          return getPlot(id, tool);
        })
        .then((response) => {
          handleDataUpdating(response.data.tool, response.data.modifications, response.data.warnings);
        })
        .catch((error) => {
          handleErrorSetting(error);
        });
    }
  }, [id, tool, handleDataSetting, handleDataUpdating, handleErrorSetting]);

  useEffect(() => {
    fetchData();
  }, [id]);

  return {
    toolData,
    parameters,
    modifications,
    warnings,
    errors,
    serverError,
    setParameters,
    handleErrorSetting,
    handleDataUpdating,
    multipleWidgetSetter,
  };
}
